import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createEntryOrder } from "@/lib/api";

const fmt = (v) => (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const EXPIRY = [["", "Until cancelled"], ["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["custom", "Custom"]];

const Seg = ({ value, onChange, options, testid }) => (
  <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v || "none"}`}
        className={`py-1.5 text-xs font-mono border transition-colors ${value === v ? "border-bt-gold bg-bt-gold text-bt-bg font-semibold" : "border-bt-border-subtle text-bt-text-tertiary hover:text-bt-text-primary"}`}>{l}</button>
    ))}
  </div>
);

export const EntryOrderForm = ({ stock, positionType, livePrice, onDone }) => {
  const isShort = positionType === "short";
  const [dir, setDir] = useState(isShort ? "above" : "below");
  const [mode, setMode] = useState("percent");
  const [pct, setPct] = useState("5");
  const [price, setPrice] = useState("");
  const [shares, setShares] = useState("");
  const [stop, setStop] = useState("");
  const [expiry, setExpiry] = useState("");
  const [customDays, setCustomDays] = useState("14");
  const [busy, setBusy] = useState(false);

  const p = parseFloat(pct), n = parseFloat(shares), sp = parseFloat(stop);
  const trigger = mode === "percent" ? (livePrice ? livePrice * (1 + (dir === "below" ? -p : p) / 100) : 0) : parseFloat(price);
  const wrongSide = livePrice && trigger && (dir === "below" ? trigger >= livePrice : trigger <= livePrice);
  const days = expiry === "custom" ? parseInt(customDays, 10) : expiry ? parseInt(expiry, 10) : null;
  const valid = n > 0 && trigger > 0 && !wrongSide && (!stop || (sp >= 0.5 && sp <= 50)) && (days === null || (days >= 1 && days <= 365));
  const stopPx = valid && sp ? trigger * (isShort ? 1 + sp / 100 : 1 - sp / 100) : null;
  const verb = isShort ? "Short" : "Buy";

  const submit = async () => {
    setBusy(true);
    try {
      await createEntryOrder({
        symbol: stock.symbol, name: stock.name, asset_type: stock.asset_type || "stock", position_type: positionType, shares: n,
        trigger_mode: mode, direction: dir,
        ...(mode === "percent" ? { trigger_pct: dir === "below" ? -p : p } : { trigger_price: trigger }),
        stop_pct: stop ? sp : null, expires_days: days,
      });
      toast.success(`${verb} order set: ${n} ${stock.symbol} when it ${dir === "below" ? "falls to" : "rises to"} $${fmt(trigger)}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't place order");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="entry-order-form">
      <div>
        <Label className="text-bt-text-secondary font-mono text-xs">{verb} when the price</Label>
        <div className="mt-1"><Seg value={dir} onChange={setDir} testid="entry-dir" options={[["below", "Falls to (dip)"], ["above", "Rises to (breakout)"]]} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-bt-text-secondary font-mono text-xs">Level</Label>
            <div className="flex text-[10px] font-mono border border-bt-border-subtle">
              {[["percent", "%"], ["price", "$"]].map(([m, l]) => (
                <button key={m} type="button" onClick={() => setMode(m)} data-testid={`entry-mode-${m}`}
                  className={`px-2 ${mode === m ? "bg-bt-gold text-bt-bg" : "text-bt-text-tertiary"}`}>{l}</button>
              ))}
            </div>
          </div>
          {mode === "percent"
            ? <Input type="number" min="0.1" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="entry-pct-input" />
            : <Input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={fmt(livePrice)} className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="entry-price-input" />}
        </div>
        <div>
          <Label className="text-bt-text-secondary font-mono text-xs">Shares</Label>
          <Input type="number" min="0" step="any" value={shares} onChange={(e) => setShares(e.target.value)} placeholder="e.g., 10" className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="entry-shares-input" />
        </div>
      </div>
      <div>
        <Label className="text-bt-text-secondary font-mono text-xs">Protective stop-loss % after fill (optional)</Label>
        <Input type="number" min="0.5" max="50" step="0.5" value={stop} onChange={(e) => setStop(e.target.value)} placeholder="e.g., 5" className="mt-1 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="entry-stop-input" />
      </div>
      <div>
        <Label className="text-bt-text-secondary font-mono text-xs">Order stays active</Label>
        <div className="mt-1"><Seg value={expiry} onChange={setExpiry} testid="entry-expiry" options={EXPIRY} /></div>
        {expiry === "custom" && (
          <div className="flex items-center gap-2 mt-2">
            <Input type="number" min="1" max="365" value={customDays} onChange={(e) => setCustomDays(e.target.value)} className="w-24 bg-bt-surface-2 border-bt-border-subtle font-mono" data-testid="entry-custom-days-input" />
            <span className="text-xs text-bt-text-tertiary font-mono">days</span>
          </div>
        )}
      </div>
      <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 font-mono text-xs space-y-1" data-testid="entry-preview">
        <p className="flex justify-between"><span className="text-bt-text-tertiary">Live price</span><span>{livePrice ? `$${fmt(livePrice)}` : "—"}</span></p>
        <p className="flex justify-between"><span className="text-bt-text-tertiary">Triggers at</span><span className="text-bt-gold" data-testid="entry-trigger-price">{trigger > 0 ? `$${fmt(trigger)}` : "—"}</span></p>
        {valid && <p className="flex justify-between"><span className="text-bt-text-tertiary">Est. cost</span><span>${fmt(trigger * n)}</span></p>}
        {stopPx && <p className="flex justify-between"><span className="text-bt-text-tertiary">Stop after fill</span><span className="text-bt-negative">${fmt(stopPx)}</span></p>}
      </div>
      {wrongSide && <p className="text-[11px] text-bt-negative" data-testid="entry-error">Level must be {dir === "below" ? "below" : "above"} the live price — or use Buy now.</p>}
      <Button onClick={submit} disabled={!valid || busy} className="w-full bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body" data-testid="entry-submit-btn">
        {busy ? "Placing…" : `Place auto-${verb.toLowerCase()} order`}
      </Button>
    </div>
  );
};
